import Button from "@mui/material/Button";
import Footer from "./components/Footer.js";
import React, { useEffect, useState } from "react";
import { DataGrid } from "@mui/x-data-grid";
import { useNavigate } from "react-router-dom";
import { buildDoc } from "./utils/pdfReceipt.js";
import { LinearProgress, ToggleButton, ToggleButtonGroup } from "@mui/material";
import isAuthenticated from "../../components/Utils/isAuthenticated.js";
import ExamLogSheetPanel from "./components/ExamLogSheetPanel.js";

export default function ViewGradingRequests() {
	const [rowList, setRowList] = useState([]);
	const [fetched, setFetched] = useState(false);
	const [loading, setLoading] = useState(false);
	const [selectedView, setSelectedView] = useState("requests");
	const navigate = useNavigate();
	const auth = isAuthenticated();
	const access = auth.access || [];
	const canViewExamLog = access.includes("ADMIN");
	const isExamLogView = canViewExamLog && selectedView === "examLog";

	useEffect(() => {
		let token = sessionStorage.getItem("token");
		let ccid = sessionStorage.getItem("ccid");
		if (!fetched) {
			setFetched(true);
			refreshList(token, ccid);
		}
	}, [fetched]);

	async function refreshList(token, ccid) {
		setLoading(true);
		let url = process.env.REACT_APP_BACKEND_HOST.concat("/scanner/requisitions_by_ccid/" + ccid);
		const response = await fetch(url, {
			headers: {
				"Content-Type": "application/json",
				Accept: "application/json",
				Authorization: `Token ${token}`,
			},
		});

		let output = await response.json();

		setRowList(output.results);
		setLoading(false);
	}

	const handleModify = (row) => {
		navigate(`/test-scoring/request-for-service?reqnum=${row.reqnum}`);
	};

	const handleViewChange = (_event, nextView) => {
		if (nextView) {
			setSelectedView(nextView);
		}
	};

	const handlePrint = async (row) => {
		//set scanner request state to those of the row
		let assign2StateValues = {};
		if (Number(row.program) === 4) {
			const exam_type = row.notes?.split(":")[2]?.trim();
			let examDateValue = new Date(row.received);
			let part1 = examDateValue.toISOString().split("T")[0];
			let part2 = examDateValue.toISOString().split("T")[1].split("-")[0];
			let part2Formatted = part2.substring(0, 5);
			examDateValue = part1 + "T" + part2Formatted;
			assign2StateValues = {
				booklets: row.booklets,
				sheets: row.sheets,
				sided: row.sided === "double" ? "Double sided" : "Single sided",
				examDate: examDateValue,
				exam_type,
				tsqs_upload: row.tsqs_upload === 1 ? "upload" : "doNotUpload",
			};
		}

		const payload = {
			a: row.a,
			b: row.b,
			c: row.c,
			caddr2: row.caddr2,
			caddr3: row.caddr3,
			class_ids: row.class_ids,
			courseTitle: row.coursetitle,
			d: row.d,
			delivery: row.delivery,
			deptID: row.dept,
			e: row.e,
			email1: row.email1,
			f: row.f,
			hqNum: row.hqnum,
			instructorName: row.instructor_name,
			k: row.k,
			l: row.l,
			lqNum: row.lqnum,
			m: row.m,
			n: row.n,
			nKeys: row.nkeys,
			notes: row.notes,
			o: row.o,
			p: row.p,
			phone: row.phone,
			pocketflag: row.pocketflag,
			program: row.program,
			qLen: row.qlen,
			reqnum: row.reqnum,
			rqdate: row.rqdate,
			scanned: row.scanned,
			speedcode: row.speedcode,
			status: row.status,
			term: row.term,
			userid: row.userid,
			...assign2StateValues,
		};

		//call print function here
		const doc = buildDoc(payload, row.reqnum);

		// Optionally, if you want to auto-print:
		doc.autoPrint();

		// Save the PDF (this will trigger a download)
		doc.save("Test Scoring Receipt " + row.reqnum + ".pdf");
	};

	const renderStatusBadge = (status) => {
		if (status === "Received") {
			return <span className="bg-secondaryIndigo text-white px-2 py-1 rounded-md">{status}</span>;
		}
		if (status === "Loaded") {
			return <span className="bg-secondaryGreen text-white px-2 py-1 rounded-md">{status}</span>;
		}
		return <span>{status}</span>;
	};

	const columns = [
		{
			field: "reqnum",
			headerName: "Req #",
			type: "string",
			width: 90,
		},
		{
			field: "rqdate",
			headerName: "Date",
			type: "string",
			renderCell: (params) => {
				return params.value ? params.value.split("T")[0] : "";
			},
			width: 100,
		},
		{
			field: "term",
			headerName: "Term",
			width: 60,
		},

		{
			field: "program",
			headerName: "Program",
			width: 85,
			renderCell: (params) => {
				if (String(params.value) === "1") {
					return "MC";
				} else if (String(params.value) === "2") {
					return "MR";
				} else if (String(params.value) === "4") {
					return "Assign2 ";
				} else {
					return "N/A";
				}
			},
		},
		{
			field: "coursetitle",
			headerName: "Course",
			flex: access.includes("ADMIN") ? 0.5 : 1,
			renderCell: (params) => {
				return (
					<span title={params.value}>
						{params.value}
						{params.row.class_ids.includes(",") && (
							<span className="text-xs text-gray-700  p-1 rounded-full">
								{"+" + (params.row.class_ids.split(",").length - 1)}
							</span>
						)}
					</span>
				);
			},
		},
		{
			field: "instructor_name",
			headerName: "Instructor",
			flex: access.includes("ADMIN") ? 0.5 : 0,
			width: 150,
		},
		{
			field: "status",
			headerName: "Status",
			headerAlign: "center",
			align: "center",
			type: "string",
			width: 100,
			renderCell: (params) => {
				if (params.value === "Received") {
					return (
						<span className="bg-secondaryIndigo text-white px-2 py-1 rounded-md">
							{params.value}
						</span>
					);
				} else if (params.value === "Loaded") {
					return (
						<span className="bg-secondaryGreen text-white px-2 py-1 rounded-md">
							{params.value}
						</span>
					);
				} else {
					return <span>{params.value}</span>;
				}
			},
		},
		{
			field: "modify",
			headerName: "Edit",
			headerAlign: "center",
			align: "center",
			renderCell: (params) => {
				return (
					<Button
						disabled={params.row.status !== "Received"}
						variant="contained"
						onClick={() => handleModify(params.row)}
					>
						Modify
					</Button>
				);
			},
		},
		{
			field: "print",
			headerName: "Print",
			headerAlign: "center",
			align: "center",
			renderCell: (params) => {
				return (
					<Button variant="outlined" onClick={() => handlePrint(params.row)}>
						Receipt
					</Button>
				);
			},
		},
	];
	return (
		<div className="flex flex-col items-center justify-start min-h-[80dvh] bg-digitalWhite">
			<main className="flex w-full flex-col items-center min-h-[90dvh]">
				<div
					className={`relative flex w-full ${access.includes("ADMIN") ? "max-w-7xl" : "max-w-4xl"} flex-col items-center border-2 my-8 rounded-md border-t-2 bg-white p-10 max-h-[90dvh]`}
				>
					<p className="text-lg font-medium pb-8">View Scanning Requests</p>
					<div className="w-full min-h-10 flex flex-col gap-2 md:flex-row md:items-center md:justify-between mb-2">
						{canViewExamLog ? (
							<ToggleButtonGroup
								size="small"
								exclusive
								value={selectedView}
								onChange={handleViewChange}
								aria-label="Scanning request view"
							>
								<ToggleButton value="requests" aria-label="Show requests">
									Requests
								</ToggleButton>
								<ToggleButton value="examLog" aria-label="Show exam log">
									Exam Log
								</ToggleButton>
							</ToggleButtonGroup>
						) : (
							<div />
						)}
						<Button variant="outlined" onClick={() => navigate("/request-for-service")}>
							+ New Request
						</Button>
					</div>
					{isExamLogView ? (
						<div className="w-full h-[70dvh]">
							<ExamLogSheetPanel />
						</div>
					) : (
						<>
							<div className="w-full h-[70dvh] hidden md:block">
								<DataGrid
									initialState={{
										sorting: {
											sortModel: [{ field: "reqnum", sort: "desc" }],
										},
										columns: {
											columnVisibilityModel: {
												instructor_name: access.includes("ADMIN"),
											},
										},
									}}
									loading={loading}
									slots={{
										/* footer: CustomFooter, */
										loadingOverlay: LinearProgress,
										noRowsOverlay: () => (
											<div className="flex items-center justify-center h-full">
												No scanning requests found.
											</div>
										),
									}}
									rows={rowList}
									columns={columns}
									pageSize={10}
									rowsPerPageOptions={[]}
									getRowId={(row) => row.reqnum}
								/>
							</div>

							{/* Mobile card view */}
							<div className="w-full space-y-3 md:hidden overflow-y-scroll">
								{loading ? (
									<div className="flex items-center justify-center py-8">
										<LinearProgress className="w-full" />
									</div>
								) : rowList.length === 0 ? (
									<div className="flex items-center justify-center py-8 text-sm text-gray-600">
										No scanning requests found.
									</div>
								) : (
									rowList.map((row) => (
										<div
											key={row.reqnum}
											className="rounded-md border border-gray-200 bg-white shadow-sm p-4 flex flex-col gap-2"
										>
											<div className="flex items-center justify-between text-sm">
												<div className="font-semibold text-primary">Req #{row.reqnum}</div>
												<div className="text-gray-600">
													{row.rqdate ? row.rqdate.split("T")[0] : ""}
												</div>
											</div>
											<div className="text-sm font-semibold text-gray-900">
												{row.coursetitle}
												{row.class_ids?.includes(",") && (
													<span className="ml-1 text-xs text-gray-600">
														+{row.class_ids.split(",").length - 1}
													</span>
												)}
											</div>
											<div className="flex items-center gap-2 text-xs text-gray-700">
												<span className="font-semibold">Program:</span>
												<span>
													{String(row.program) === "1"
														? "MC"
														: String(row.program) === "2"
															? "MR"
															: String(row.program) === "4"
																? "Assign2"
																: "N/A"}
												</span>
											</div>
											<div className="flex items-center gap-2 text-xs text-gray-700">
												<span className="font-semibold">Status:</span>
												{renderStatusBadge(row.status)}
											</div>
											<div className="flex gap-2 pt-1">
												<Button
													size="small"
													variant="contained"
													disabled={row.status !== "Received"}
													onClick={() => handleModify(row)}
												>
													Modify
												</Button>
												<Button size="small" variant="outlined" onClick={() => handlePrint(row)}>
													Receipt
												</Button>
											</div>
										</div>
									))
								)}
							</div>
						</>
					)}
				</div>
			</main>
			<Footer />
		</div>
	);
}
